import { h } from 'preact';
import { useRef } from 'preact/hooks';
import { useConditionalTitle } from '../../hooks/useConditionalTitle';
import { QuickTooltip, useQuickTooltip } from '../QuickTooltip';
import { COL_PARTNUM, COL_PRICE, COL_SHIPS } from './model';
import type { ColumnAlign, ColumnDef, SortDirection, SortState } from './model';

interface ColumnHeaderProps {
  column: ColumnDef;
  filterValues: string[];
  sort: SortState | null;
  toggleSort: (key: string) => void;
  filter: Set<string> | undefined;
  openFilterKey: string | null;
  onToggleFilter: (key: string, anchor: HTMLElement) => void;
  minWidth: number;
  align: ColumnAlign;
}

export function ColumnHeader({
  column,
  filterValues,
  sort,
  toggleSort,
  filter,
  openFilterKey,
  onToggleFilter,
  minWidth,
  align,
}: ColumnHeaderProps) {
  const labelRef = useRef<HTMLSpanElement>(null);
  const tooltipText = useConditionalTitle(labelRef, column.label);
  const { tooltip, showTooltip, hideTooltip } = useQuickTooltip(tooltipText);
  const isFilterActive = !!filter && filterValues.length > 0 && filter.size < filterValues.length;
  const isOpen = openFilterKey === column.key;
  const sortDir: SortDirection | null = sort?.key === column.key ? sort.dir : null;
  const hasFilterButton = column.filterable && filterValues.length > 1;
  const sortIndicator = sortDir === 'asc' ? '↑' : sortDir === 'desc' ? '↓' : '↕';

  return (
    <th
      class={`ts-family-th ts-family-th--align-${align} ${column.key === COL_PARTNUM ? 'ts-family-th--partnum' : ''} ${column.key === COL_PRICE ? 'ts-family-th--price' : ''} ${column.key === COL_SHIPS ? 'ts-family-th--ships' : ''} ${sortDir ? `ts-family-th--sort-${sortDir}` : ''} ${isFilterActive ? 'ts-family-th--filtered' : ''}`}
      data-th-key={column.key}
      aria-sort={column.sortable ? (sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none') : undefined}
      style={{ minWidth: `${minWidth}px`, width: `${minWidth}px` }}
    >
      <div
        class={`ts-family-th-inner ${hasFilterButton ? 'ts-family-th-inner--has-filter' : ''}`}
        onMouseEnter={(e) => showTooltip(e.currentTarget)}
        onMouseLeave={hideTooltip}
        onFocus={(e) => showTooltip(e.currentTarget)}
        onBlur={hideTooltip}
      >
        {column.sortable ? (
          <button
            class="ts-family-th-sort"
            type="button"
            onClick={() => toggleSort(column.key)}
            aria-label={`Sort by ${column.label}${sortDir ? `, currently ${sortDir === 'asc' ? 'ascending' : 'descending'}` : ''}`}
          >
            <span ref={labelRef} class="ts-family-th-label">{column.label}</span>
            <span class={`ts-family-th-sort-ind ${sortDir ? '' : 'ts-family-th-sort-ind--idle'}`} aria-hidden="true">
              {sortIndicator}
            </span>
          </button>
        ) : (
          <span ref={labelRef} class="ts-family-th-label">{column.label}</span>
        )}
        {hasFilterButton && (
          <button
            class="ts-family-th-filter"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleFilter(column.key, e.currentTarget as HTMLElement);
            }}
            aria-label={`Filter ${column.label}`}
            aria-pressed={isFilterActive}
            aria-expanded={isOpen}
          >
            <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
              <path d="M2 3h12l-4.5 6v4l-3-1.5V9L2 3z" fill="currentColor" />
            </svg>
            {isFilterActive && <span class="ts-family-th-filter-dot" aria-hidden="true" />}
          </button>
        )}
        <QuickTooltip tooltip={tooltip} />
      </div>
    </th>
  );
}
